import React from 'react';
import SqftIcon from 'components/icon/sqft-icon';
import BathIcon from 'components/icon/bath-icon';
import BedIcon from 'components/icon/bed-icon';
import SqftText from 'components/sqft-text';
import styles from './style.module.scss';
import { listingCardIds } from 'constants/test-constants';
import { buildClassName } from 'utils/build-class-name';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { localizeSqftLabel } from 'ui/sqft';

import type { SimilarListingCardData } from 'components/pages/address/public/components/similar-listings-sections/similar_listing_card_data';
import { PropsWithTenantId } from '@tenants/types';
import { isExpTenant } from '@tenants';

export type ListingBedBathSqftLabelsData = {
  bedrooms: number;
  bathrooms: number | null;
  avgSqft: number | null;
  sqftLabel: string;
};

export type ListingBedBathSqftLabelsProps = PropsWithTenantId< {
  listing: ListingBedBathSqftLabelsData;
  listingToCompare?: ListingBedBathSqftLabelsData | SimilarListingCardData;
  obfuscate: boolean;
  hideSquareFootage?: boolean;
}>;

export default function ListingBedBathSqftLabels({ listing, listingToCompare, obfuscate, tenantId, hideSquareFootage }: ListingBedBathSqftLabelsProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const useExpStyle = isExpTenant(tenantId);

  let differenceInBedrooms = 0;
  let differenceInBathrooms = 0;
  let differenceInSqft = 0;

  if (listingToCompare) {
    differenceInBedrooms = listing.bedrooms - listingToCompare.bedrooms;
    differenceInBathrooms = Number(listing.bathrooms) - Number(listingToCompare.bathrooms);
    differenceInSqft = Number(listing.avgSqft) - Number(listingToCompare.avgSqft);
  }

  const getPlusMinusPrefix = (differenceInStat: number) => differenceInStat > 0 ? '+' : '';

  const getArrowClassName = (differenceInStat: number) => {
    if (differenceInStat > 0) {
      return 'arrow-up';
    } else if (differenceInStat < 0) {
      return 'arrow-down';
    }
    return '';
  };

  const getDifferenceLabel = (differenceInStat: number) => {
    if (differenceInStat !== 0) {
      return `(${getPlusMinusPrefix(differenceInStat)} ${differenceInStat})`;
    }
  };

  // eXp surfaces the abbreviated forms ("3 Bd"), everyone else the long ones ("3 Bed").
  // `difference` stays empty because the comparison label is rendered as its own node
  // below, alongside the up/down arrow.
  const bedroomsLabel = useExpStyle
    ? t(LOCALE_COMMON_PATHS.LISTING_CARD_NUMBER_OF_BEDROOMS, { count: listing.bedrooms, difference: '', defaultValue: `${listing.bedrooms} Bd` })
    : t(LOCALE_COMMON_PATHS.LISTING_CARD_NUMBER_OF_BEDROOMS_LONG, { count: listing.bedrooms, difference: '', defaultValue: `${listing.bedrooms} Bed` });

  const bathroomsLabel = useExpStyle
    ? t(LOCALE_COMMON_PATHS.LISTING_CARD_NUMBER_OF_BATHROOMS, { count: listing.bathrooms, difference: '', defaultValue: `${listing.bathrooms} Ba` })
    : t(LOCALE_COMMON_PATHS.LISTING_CARD_NUMBER_OF_BATHROOMS_LONG, { count: listing.bathrooms, difference: '', defaultValue: `${listing.bathrooms} Bath` });

  const renderModalDialog = () => {
    return (
      <div className={buildClassName(styles.row, useExpStyle && styles.exp)}>
        <div>
          <p className={styles['listing-detail']} data-testid={listingCardIds.listingBeds}>
            <BedIcon className={styles.icon} />
            {obfuscate ? '-' : bedroomsLabel}

            {!obfuscate && listingToCompare &&
                <>
                  {getDifferenceLabel(differenceInBedrooms)}
                  <span className={styles[getArrowClassName(differenceInBedrooms)]} />
                </>
            }
          </p>
          <p className={styles['listing-detail']} data-testid={listingCardIds.listingBathrooms}>
            <BathIcon className={styles.icon} />
            {obfuscate ? '-' : bathroomsLabel}
            {!obfuscate && listingToCompare &&
                <>
                  {getDifferenceLabel(differenceInBathrooms)}
                  <span className={styles[getArrowClassName(differenceInBathrooms)]} />
                </>
            }
          </p>
          <p className={styles['listing-detail']} data-testid={listingCardIds.listingSqft}>
            <SqftIcon className={styles.icon} />
            {obfuscate || hideSquareFootage ? '-' : <SqftText squareFootageLabel={listing.sqftLabel} render={formattedSqft => <>{localizeSqftLabel(formattedSqft, t)}</>} />}
            {!obfuscate && listingToCompare && <span className={styles[getArrowClassName(differenceInSqft)]} />}
          </p>
        </div>
      </div>
    );
  };

  return renderModalDialog();
}
